import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Suspense, useRef } from "react";
import { Button } from "@/components/ui/button";
import { NativeArticle } from "@/components/site/NativeArticle";
import { ArrowLeft } from "lucide-react";
import {
  newsArticleQueryOptions as articleQueryOptions,
  type NewsArticleDetail as Article,
} from "@/lib/news.queries";

export const Route = createFileRoute("/news_/$slug")({
  loader: async ({ context, params }) => {
    const article = await context.queryClient.ensureQueryData(articleQueryOptions(params.slug));
    if (!article || !article.body_html) throw notFound();
    return article;
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: `${loaderData?.title ?? "Article"} — Aesthetic Insights — Medical Insight` },
      {
        name: "description",
        content: loaderData?.excerpt ?? "Aesthetic Insights from Medical Insight, Inc.",
      },
    ],
  }),
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-4 py-24 text-center">
      <h1 className="text-2xl font-bold">Article not found</h1>
      <p className="mt-2 text-muted-foreground">
        It may have been unpublished or the link is out of date.
      </p>
      <Button asChild className="mt-6">
        <Link to="/news">Back to Aesthetic Insights</Link>
      </Button>
    </div>
  ),
  component: ArticlePage,
});

function ArticlePage() {
  return (
    <Suspense fallback={<div className="mx-auto max-w-5xl px-4 py-16">Loading…</div>}>
      <ArticleContent />
    </Suspense>
  );
}

/**
 * Imported Aesthetic Insights bodies are full newsletter-email HTML (their own
 * styles, table layouts). Rendered inside a sandboxed iframe so that styling
 * cannot bleed into the site (or vice versa); scripts stay disabled. Clean
 * locally-authored / converted bodies render as normal site content instead.
 * body_format (migration 019) makes the choice explicit; rows without it fall
 * back to this sniff.
 */
function looksLikeEmailHtml(body: string) {
  const head = body.slice(0, 2000).toLowerCase();
  return head.includes("<table") || head.includes("xmlns:o=") || head.includes("<!doctype");
}

function resolveFormat(article: Article): "email" | "clean" {
  if (article.body_format === "email" || article.body_format === "clean") {
    return article.body_format;
  }
  return looksLikeEmailHtml(article.body_html ?? "") ? "email" : "clean";
}

function ArticleContent() {
  const { slug } = Route.useParams();
  const { data: article } = useSuspenseQuery(articleQueryOptions(slug));
  const frameRef = useRef<HTMLIFrameElement>(null);
  if (!article || !article.body_html) return null;

  const format = resolveFormat(article);

  return (
    <div className="border-t bg-secondary/30">
      <div className="mx-auto max-w-5xl px-4 py-6 sm:py-8">
        <div className="mb-4 flex items-center justify-between gap-4">
          <Button asChild variant="ghost" size="sm">
            <Link to="/news">
              <ArrowLeft className="mr-1 h-4 w-4" /> Aesthetic Insights
            </Link>
          </Button>
          {/* Native articles show the date in the masthead band instead. */}
          {format === "email" && (
            <span className="text-sm text-muted-foreground">
              {new Date(article.published_at.replace(" ", "T")).toLocaleDateString(undefined, {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </span>
          )}
        </div>

        {format === "email" ? (
          <>
            <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">{article.title}</h1>
            {article.excerpt && (
              <p className="mt-3 max-w-3xl text-muted-foreground">{article.excerpt}</p>
            )}
            <div className="mt-8 overflow-hidden rounded-lg border bg-white">
              <iframe
                ref={frameRef}
                title={article.title}
                sandbox="allow-same-origin allow-popups"
                srcDoc={article.body_html}
                className="w-full"
                style={{ minHeight: "60vh", border: 0 }}
                onLoad={() => {
                  const f = frameRef.current;
                  const doc = f?.contentDocument;
                  if (!f || !doc) return;
                  // links inside the email should open in a new tab
                  doc.querySelectorAll("a").forEach((a) => a.setAttribute("target", "_blank"));
                  const fit = () => {
                    f.style.height = `${Math.max(doc.documentElement.scrollHeight, 400)}px`;
                  };
                  fit();
                  // images load progressively — refit a few times
                  setTimeout(fit, 500);
                  setTimeout(fit, 2000);
                }}
              />
            </div>
          </>
        ) : (
          <NativeArticle
            title={article.title}
            excerpt={article.excerpt}
            published_at={article.published_at}
            image_url={article.image_url}
            header_image_url={article.header_image_url}
            body_html={article.body_html}
            kicker={article.category}
            showBoard={article.show_advisory_board !== false}
          />
        )}
      </div>
    </div>
  );
}
